import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Badge
} from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";

const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const capitalizeAllergenName = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const normalizeAllergen = (allergen = {}) => ({
  allergen_id: Number(allergen.allergen_id),
  allergen_name: allergen.allergen_name || ""
});

const AllergenModal = ({ isOpen, toggle, onSave, selectedAllergens = [] }) => {
  const [selected, setSelected] = useState([]);
  const [showAddNew, setShowAddNew] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const debouncedSearch = useDebounce(search, 600);

  useEffect(() => {
    if (isOpen) {
      setSelected((selectedAllergens || []).map(normalizeAllergen));
      setSearch("");
      setSuggestions([]);
      setShowAddNew(false);
    }
  }, [isOpen, selectedAllergens]);

  useEffect(() => {
    if (!showAddNew) {
      return;
    }

    if (!debouncedSearch.trim()) {
      setSuggestions([]);
      return;
    }

    setLoading(true);

    postDataAPI({
      serviceName: "item_data",
      op: "get_allergens",
      search: debouncedSearch
    })
      .then((res) => {
        if (res.status === "ok") {
          setSuggestions((res.data || []).map(normalizeAllergen));
        } else {
          setSuggestions([]);
        }
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [debouncedSearch, showAddNew]);

  const addAllergen = (allergen) => {
    const normalized = normalizeAllergen(allergen);
    if (!normalized.allergen_id || !normalized.allergen_name) return;
    if (selected.some((a) => a.allergen_id === normalized.allergen_id)) return;

    setSelected([...selected, normalized]);
    setSearch("");
    setSuggestions([]);
    setShowAddNew(false);
  };

  const createAndAddAllergen = async () => {
    const name = capitalizeAllergenName(search);
    if (!name) {
      alert("Please enter an allergen name");
      return;
    }

    const exactMatch = suggestions.find(
      (item) => item.allergen_name.toLowerCase() === name.toLowerCase()
    );
    if (exactMatch) {
      addAllergen(exactMatch);
      return;
    }

    try {
      setCreating(true);
      const res = await postDataAPI({
        serviceName: "item_data",
        op: "create_allergen",
        allergen_name: name
      });

      if (res.status === "ok" && res.data) {
        addAllergen(res.data);
      } else {
        alert(res.message || "Failed to create allergen");
      }
    } catch (err) {
      alert("Failed to create allergen");
    } finally {
      setCreating(false);
    }
  };

  const removeAllergen = (id) => {
    setSelected((prev) => prev.filter((a) => a.allergen_id !== Number(id)));
  };

  const searchTerm = capitalizeAllergenName(search);
  const availableSuggestions = suggestions.filter(
    (item) => !selected.some((a) => a.allergen_id === item.allergen_id)
  );
  const hasExactMatch = [...suggestions, ...selected].some(
    (item) => item.allergen_name.toLowerCase() === searchTerm.toLowerCase()
  );
  const showCreateOption = searchTerm.length > 0 && !hasExactMatch && !loading;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        ⚠️ Add Allergens
        <div className="text-muted small">Select allergens for this flashcard</div>
      </ModalHeader>

      <ModalBody>
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Badge color="warning">{selected.length} selected</Badge>
            <Button size="sm" outline onClick={() => setShowAddNew(!showAddNew)}>
              + Add New Allergen
            </Button>
          </div>

          {selected.length === 0 && (
            <div className="text-muted small">No allergens selected</div>
          )}

          {selected.map((allergen) => (
            <div
              key={allergen.allergen_id}
              className="d-flex justify-content-between align-items-center border rounded p-2 mb-2"
            >
              <span>{allergen.allergen_name}</span>
              <span
                style={{ cursor: "pointer" }}
                onClick={() => removeAllergen(allergen.allergen_id)}
              >
                ❌
              </span>
            </div>
          ))}
        </div>

        {showAddNew && (
          <div className="border rounded p-3 mt-3 bg-light">
            <Input
              placeholder="Search allergen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && showCreateOption) {
                  e.preventDefault();
                  createAndAddAllergen();
                }
              }}
              autoFocus
            />

            {loading && (
              <div className="small text-muted mt-1">Searching...</div>
            )}

            {(availableSuggestions.length > 0 || showCreateOption) && (
              <div
                className="border rounded mt-2 bg-white"
                style={{ maxHeight: 200, overflowY: "auto" }}
              >
                {availableSuggestions.map((item) => (
                  <div
                    key={item.allergen_id}
                    onClick={() => addAllergen(item)}
                    className="p-2 d-flex justify-content-between align-items-center border-bottom"
                    style={{ cursor: "pointer" }}
                  >
                    <span>{item.allergen_name}</span>
                  </div>
                ))}

                {showCreateOption && (
                  <div
                    onClick={creating ? undefined : createAndAddAllergen}
                    className="p-2 d-flex justify-content-between align-items-center border-bottom text-primary"
                    style={{ cursor: creating ? "not-allowed" : "pointer" }}
                  >
                    <span>
                      {creating ? "Creating..." : `+ Create "${searchTerm || search.trim()}"`}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="text-end mt-2">
              <Button
                size="sm"
                outline
                onClick={() => {
                  setShowAddNew(false);
                  setSearch("");
                  setSuggestions([]);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button outline onClick={toggle}>Cancel</Button>
        <Button
          color="primary"
          onClick={() => {
            onSave(selected);
            toggle();
          }}
        >
          Save Allergens
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AllergenModal;
